/**
 * FSD Part 11 — Property Management. Sections follow the FSD's feature
 * specifications 11.2.1–11.2.12 (PMS-F01…PMS-F12) in order.
 *
 * Item ids are stable and must never change: favourites and recents are
 * keyed off them in localStorage. Section ids (`s-<fsd>`) are new in C17.
 *
 * Detail routes (a single reservation, folio or room) are reached from their
 * own list, never from the sidebar directly.
 */
import type { NavGroup } from '../types'
import { PMS_PERMISSIONS } from '@/modules/pms/permissions'
import { HOUSEKEEPING_PERMISSIONS } from '@/modules/pms/housekeeping/permissions'
import { PMS_OPS_PERMISSIONS } from '@/modules/pms/ops/permissions'

export const P11_PMS_GROUPS: NavGroup[] = [
  {
    id: 's-11.2.1',
    kind: 'feature',
    fsd: '11.2.1',
    code: 'PMS-F01',
    labelKey: 'nav.fsd.s11_2_1.short',
    fullTitleKey: 'nav.fsd.s11_2_1.full',
    items: [
      {
        id: 'pms-reservations',
        labelKey: 'pms.routeTitles.reservations',
        to: '/pms/reservations',
        icon: 'inbox',
        permission: PMS_PERMISSIONS.reservationRead,
      },
    ],
  },
  {
    id: 's-11.2.2',
    kind: 'feature',
    fsd: '11.2.2',
    code: 'PMS-F02',
    labelKey: 'nav.fsd.s11_2_2.short',
    fullTitleKey: 'nav.fsd.s11_2_2.full',
    items: [
      {
        id: 'pms-group-blocks',
        labelKey: 'pms.routeTitles.groupBlocks',
        to: '/pms/group-blocks',
        icon: 'users',
        permission: PMS_PERMISSIONS.reservationRead,
      },
    ],
  },
  {
    id: 's-11.2.3',
    kind: 'feature',
    fsd: '11.2.3',
    code: 'PMS-F03',
    labelKey: 'nav.fsd.s11_2_3.short',
    fullTitleKey: 'nav.fsd.s11_2_3.full',
    items: [
      {
        id: 'pms-guests',
        labelKey: 'pms.routeTitles.guests',
        to: '/pms/guests',
        icon: 'user',
        permission: PMS_PERMISSIONS.guestRead,
      },
      {
        id: 'pms-front-desk',
        labelKey: 'pms.ops.routeTitles.frontDesk',
        to: '/pms/front-desk',
        icon: 'monitor',
        permission: PMS_OPS_PERMISSIONS.frontDeskRead,
      },
    ],
  },
  {
    id: 's-11.2.4',
    kind: 'feature',
    fsd: '11.2.4',
    code: 'PMS-F04',
    labelKey: 'nav.fsd.s11_2_4.short',
    fullTitleKey: 'nav.fsd.s11_2_4.full',
    items: [
      {
        id: 'pms-housekeeping-boards',
        labelKey: 'pms.housekeeping.routeTitles.boards',
        to: '/pms/housekeeping/boards',
        icon: 'dashboard',
        permission: HOUSEKEEPING_PERMISSIONS.read,
      },
      {
        id: 'pms-housekeeping-rooms',
        labelKey: 'pms.housekeeping.routeTitles.rooms',
        to: '/pms/housekeeping/rooms',
        icon: 'building',
        permission: HOUSEKEEPING_PERMISSIONS.read,
      },
      {
        id: 'pms-housekeeping-faults',
        labelKey: 'pms.housekeeping.routeTitles.faults',
        to: '/pms/housekeeping/faults',
        icon: 'alert',
        permission: HOUSEKEEPING_PERMISSIONS.read,
      },
      {
        id: 'pms-housekeeping-out-of-order',
        labelKey: 'pms.housekeeping.routeTitles.outOfOrder',
        to: '/pms/housekeeping/out-of-order',
        icon: 'ban',
        permission: PMS_PERMISSIONS.roomOutOfOrder,
      },
    ],
  },
  {
    id: 's-11.2.5',
    kind: 'feature',
    fsd: '11.2.5',
    code: 'PMS-F05',
    labelKey: 'nav.fsd.s11_2_5.short',
    fullTitleKey: 'nav.fsd.s11_2_5.full',
    items: [
      {
        id: 'pms-folios',
        labelKey: 'pms.ops.routeTitles.folios',
        to: '/pms/folios',
        icon: 'history',
        permission: PMS_OPS_PERMISSIONS.folioRead,
      },
      {
        id: 'pms-deposits',
        labelKey: 'pms.ops.routeTitles.deposits',
        to: '/pms/deposits',
        icon: 'scale',
        permission: PMS_OPS_PERMISSIONS.folioRead,
      },
    ],
  },
  {
    id: 's-11.2.6',
    kind: 'feature',
    fsd: '11.2.6',
    code: 'PMS-F06',
    labelKey: 'nav.fsd.s11_2_6.short',
    fullTitleKey: 'nav.fsd.s11_2_6.full',
    items: [
      {
        id: 'pms-properties',
        labelKey: 'pms.routeTitles.properties',
        to: '/pms/properties',
        icon: 'building',
        permission: PMS_PERMISSIONS.propertyRead,
      },
      {
        id: 'pms-rate-plans',
        labelKey: 'pms.routeTitles.ratePlans',
        to: '/pms/rate-plans',
        icon: 'star',
        permission: PMS_PERMISSIONS.rateRead,
      },
      {
        id: 'pms-restrictions',
        labelKey: 'pms.routeTitles.restrictions',
        to: '/pms/restrictions',
        icon: 'ban',
        permission: PMS_PERMISSIONS.rateRead,
      },
      {
        id: 'pms-availability',
        labelKey: 'pms.routeTitles.availability',
        to: '/pms/availability',
        icon: 'calendar',
        permission: PMS_PERMISSIONS.availabilityRead,
      },
      {
        id: 'pms-parity',
        labelKey: 'pms.ops.routeTitles.parity',
        to: '/pms/parity',
        icon: 'scale',
        permission: PMS_OPS_PERMISSIONS.channelRead,
      },
    ],
  },
  {
    id: 's-11.2.7',
    kind: 'feature',
    fsd: '11.2.7',
    code: 'PMS-F07',
    labelKey: 'nav.fsd.s11_2_7.short',
    fullTitleKey: 'nav.fsd.s11_2_7.full',
    items: [
      {
        id: 'pms-channels',
        labelKey: 'pms.ops.routeTitles.channels',
        to: '/pms/channels',
        icon: 'refresh',
        permission: PMS_OPS_PERMISSIONS.channelRead,
      },
      {
        id: 'pms-commission-agreements',
        labelKey: 'pms.ops.routeTitles.agreements',
        to: '/pms/commission/agreements',
        icon: 'mail',
        permission: PMS_OPS_PERMISSIONS.channelRead,
      },
      {
        id: 'pms-commission-accruals',
        labelKey: 'pms.ops.routeTitles.accruals',
        to: '/pms/commission/accruals',
        icon: 'calendar',
        permission: PMS_OPS_PERMISSIONS.channelRead,
      },
      {
        id: 'pms-commission-statements',
        labelKey: 'pms.ops.routeTitles.statements',
        to: '/pms/commission/statements',
        icon: 'inbox',
        permission: PMS_OPS_PERMISSIONS.channelRead,
      },
    ],
  },
  {
    id: 's-11.2.8',
    kind: 'feature',
    fsd: '11.2.8',
    code: 'PMS-F08',
    labelKey: 'nav.fsd.s11_2_8.short',
    fullTitleKey: 'nav.fsd.s11_2_8.full',
    items: [
      {
        id: 'pms-night-audit',
        labelKey: 'pms.ops.routeTitles.nightAudit',
        to: '/pms/night-audit',
        icon: 'moon',
        permission: PMS_OPS_PERMISSIONS.nightAuditRead,
      },
      {
        id: 'pms-settings',
        labelKey: 'pms.routeTitles.settings',
        to: '/pms/settings',
        icon: 'settings',
        permission: PMS_PERMISSIONS.propertyRead,
      },
      {
        id: 'pms-reports',
        labelKey: 'pms.ops.routeTitles.reports',
        to: '/pms/reports',
        icon: 'history',
        permission: PMS_OPS_PERMISSIONS.reportRead,
      },
    ],
  },
  {
    id: 's-11.2.9',
    kind: 'feature',
    fsd: '11.2.9',
    code: 'PMS-F09',
    labelKey: 'nav.fsd.s11_2_9.short',
    fullTitleKey: 'nav.fsd.s11_2_9.full',
    items: [
      {
        id: 'pms-services',
        labelKey: 'pms.ops.routeTitles.services',
        to: '/pms/services',
        icon: 'menu',
        permission: PMS_OPS_PERMISSIONS.serviceRead,
      },
      {
        id: 'pms-service-schedule',
        labelKey: 'pms.ops.routeTitles.schedule',
        to: '/pms/services/schedule',
        icon: 'calendar',
        permission: PMS_OPS_PERMISSIONS.serviceRead,
      },
    ],
  },
  {
    id: 's-11.2.10',
    kind: 'feature',
    fsd: '11.2.10',
    code: 'PMS-F10',
    labelKey: 'nav.fsd.s11_2_10.short',
    fullTitleKey: 'nav.fsd.s11_2_10.full',
    items: [
      {
        id: 'pms-board-plans',
        labelKey: 'pms.ops.routeTitles.boardPlans',
        to: '/pms/board-plans',
        icon: 'star',
        permission: PMS_OPS_PERMISSIONS.serviceRead,
      },
      {
        id: 'pms-unconsumed-allowances',
        labelKey: 'pms.ops.routeTitles.unconsumed',
        to: '/pms/board-allowances/unconsumed',
        icon: 'alert',
        permission: PMS_OPS_PERMISSIONS.reportRead,
      },
    ],
  },
  {
    id: 's-11.2.11',
    kind: 'feature',
    fsd: '11.2.11',
    code: 'PMS-F11',
    labelKey: 'nav.fsd.s11_2_11.short',
    fullTitleKey: 'nav.fsd.s11_2_11.full',
    items: [
      {
        id: 'pms-guest-requests',
        labelKey: 'pms.ops.routeTitles.guestRequests',
        to: '/pms/guest-requests',
        icon: 'inbox',
        permission: PMS_OPS_PERMISSIONS.requestManage,
      },
    ],
  },
  {
    id: 's-11.2.12',
    kind: 'feature',
    fsd: '11.2.12',
    code: 'PMS-F12',
    labelKey: 'nav.fsd.s11_2_12.short',
    fullTitleKey: 'nav.fsd.s11_2_12.full',
    items: [
      {
        id: 'pms-transfers',
        labelKey: 'pms.ops.routeTitles.transfers',
        to: '/pms/transfers',
        icon: 'refresh',
        permission: PMS_OPS_PERMISSIONS.serviceRead,
      },
    ],
  },
]
